import { useSnackbar } from "notistack";
import { useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { Button, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
	useAddRecipientsFromCSVMutation,
	useGetCurrentUserMailingGroupsQuery,
} from "../../../recipientsApiSlice";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const CustomSelectToolbar = (props) => {
	const { t } = useTranslation();
	const {
		data,
		error,
		isLoading: isLoadingGroups,
	} = useGetCurrentUserMailingGroupsQuery();
	const [addRecipients, { isLoading }] = useAddRecipientsFromCSVMutation();

	const { selectedRows, displayData, onRowsDelete } = props;
	const { enqueueSnackbar } = useSnackbar();
	const [selectedGroups, setSelectedGroups] = useState([]);

	const handleChange = (event) => {
		const {
			target: { value },
		} = event;
		setSelectedGroups(typeof value === "string" ? value.split(",") : value);
	};

	const submitContacts = async () => {
		const selectedIndexes = Object.keys(selectedRows.lookup);

		if (selectedGroups.length < 1) {
			enqueueSnackbar(t("select_groups_to_assign"), {
				variant: "error",
				preventDuplicate: true,
			});
			return;
		}

		const parsedData = selectedIndexes.map((currentIndex) => {
			const currentData = Object.values(displayData[currentIndex].data);
			const name = currentData[2].props.defaultValue;
			const firstName = currentData[3].props.defaultValue;
			const lastName = currentData[4].props.defaultValue;
			const nip = currentData[5].props.defaultValue;
			const email = currentData[6].props.defaultValue;
			const phone = currentData[7].props.defaultValue;

			if (!name || name.length < 1 || !email || email.length < 1) {
				enqueueSnackbar(t("fill_required_fields"), {
					variant: "error",
					preventDuplicate: true,
				});
				return null;
			}

			return { name, firstName, lastName, nip, email, phone };
		});

		const isValidParsedData = parsedData.every((item) => item !== null);
		if (!parsedData || parsedData.length < 1 || !isValidParsedData) {
			return;
		}

		await addRecipients({
			data: { groupsIds: selectedGroups, data: parsedData },
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("import_added_carriers_to_group"), {
					variant: "success",
				});
				onRowsDelete();
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([_key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	if (isLoadingGroups) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "end",
					alignItems: "center",
					height: "64px",
					columnGap: "10px",
				}}
			>
				<Box sx={{ mr: 3 }}>
					<Skeleton height={20.56} width={148} sx={{ transform: "scale(1)" }} />
				</Box>
				<Box>
					<Skeleton height={36.7} width={300} sx={{ transform: "scale(1)" }} />
				</Box>
				<Box>
					<Skeleton
						height={36.7}
						width={135.3}
						sx={{ transform: "scale(1)" }}
					/>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "end",
				alignItems: "center",
				gap: "6px",
				height: "64px",
			}}
		>
			<Box sx={{ mr: 3 }}>
				<Typography sx={{ color: "rgb(166, 166, 166)" }}>
					{t("selected_rows")}: {selectedRows.data.length} {t("of")}{" "}
					{displayData.length}
				</Typography>
			</Box>
			<FormControl sx={{ mx: 1, width: 300 }}>
				<InputLabel size="small" id="demo-multiple-checkbox-label">
					{t("groups")}
				</InputLabel>
				<Select
					size="small"
					labelId="demo-multiple-checkbox-label"
					id="demo-multiple-checkbox"
					multiple
					value={selectedGroups}
					onChange={handleChange}
					input={<OutlinedInput label={t("groups")} />}
					renderValue={(selected) => {
						return selected
							.map((value) => {
								const option = Object.entries(data.groups).find(
									([id, option]) => id === value
								);
								return option ? option[1] : "";
							})
							.join(", ");
					}}
					MenuProps={MenuProps}
				>
					{Object.entries(data.groups).map(([id, option]) => (
						<MenuItem key={id} value={id}>
							<Checkbox checked={selectedGroups.indexOf(id) > -1} />
							<ListItemText primary={option} />
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<Box sx={{ display: "flex", alignItems: "center" }}>
				<Button
					type="button"
					onClick={submitContacts}
					fullWidth
					variant="contained"
					// loading={isLoading}
				>
					{t("save_marked")}
				</Button>
			</Box>
		</Box>
	);
};

export default CustomSelectToolbar;
